import { Info } from 'lucide-react';
import type { ReactNode } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { isFailClosed } from '@/lib/agent-platform';
import { FailClosedNotice } from './fail-closed-notice';

interface ScreeningLogSectionProps {
  markdown: string | null | undefined;
  onOpenAuditTrail: () => void;
}

/**
 * Clean up the formatted report markdown by removing sections that are
 * displayed separately in the report page UI.
 */
function cleanFormattedReport(markdown: string): string {
  let cleaned = markdown;

  // Remove "Screening Log" header line (we have our own section header)
  cleaned = cleaned.replace(/^#{1,3}\s*Screening Log\s*\n+/gim, '');

  // Remove "Header Section" header (sometimes appears before Client Details)
  cleaned = cleaned.replace(/^#{1,3}\s*Header Section\s*\n+/gim, '');

  // Remove "Client Details" section entirely - matches from the header until the next
  // paragraph that starts with "The subject", "A detailed" (current wording), or
  // "A comprehensive" (retained so pre-change cached reports still parse)
  cleaned = cleaned.replace(
    /#{1,3}\s*Client Details[\s\S]*?(?=(?:The subject|A detailed|A comprehensive|Sanctions|No sanctions|\n#{1,3}\s(?!Client)))/gi,
    '',
  );

  // Fallback: Remove any remaining Client Details with key-value format
  cleaned = cleaned.replace(
    /Client Details\s*\n+(?:(?:Client Reference|Title|Full Name|Date of Birth|Age|Nationality|Address|Location|Occupation|Risk Level)[:\s][^\n]*\n*)*/gi,
    '',
  );

  // Remove "Sources" section entirely (displayed separately with better formatting)
  cleaned = cleaned.replace(/#{1,3}\s*Sources[\s\S]*?(?=\n#{1,3}\s|$)/gi, '');

  // Clean up any excessive newlines left behind
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n');

  return cleaned.trim();
}

const markdownComponents = {
  h1: ({ children }: { children?: ReactNode }) => (
    <h1 className="mb-4 mt-6 text-xl font-bold text-[#051923]">{children}</h1>
  ),
  h2: ({ children }: { children?: ReactNode }) => (
    <h2 className="mb-3 mt-5 text-lg font-semibold text-[#051923]">{children}</h2>
  ),
  h3: ({ children }: { children?: ReactNode }) => (
    <h3 className="mb-2 mt-4 text-base font-semibold text-[#051923]">{children}</h3>
  ),
  p: ({ children }: { children?: ReactNode }) => <p className="mb-3 leading-relaxed">{children}</p>,
  ul: ({ children }: { children?: ReactNode }) => (
    <ul className="mb-3 list-disc pl-5">{children}</ul>
  ),
  ol: ({ children }: { children?: ReactNode }) => (
    <ol className="mb-3 list-decimal pl-5">{children}</ol>
  ),
  li: ({ children }: { children?: ReactNode }) => <li className="mb-1">{children}</li>,
  strong: ({ children }: { children?: ReactNode }) => (
    <strong className="font-semibold text-[#051923]">{children}</strong>
  ),
  a: ({ href, children }: { href?: string; children?: ReactNode }) => (
    <a
      href={href}
      className="text-blue-600 hover:underline"
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  ),
  table: ({ children }: { children?: ReactNode }) => (
    <div className="my-4 overflow-x-auto">
      <table className="min-w-full border-collapse border border-gray-200">{children}</table>
    </div>
  ),
  thead: ({ children }: { children?: ReactNode }) => (
    <thead className="bg-gray-50">{children}</thead>
  ),
  tbody: ({ children }: { children?: ReactNode }) => <tbody>{children}</tbody>,
  tr: ({ children }: { children?: ReactNode }) => (
    <tr className="border-b border-gray-200">{children}</tr>
  ),
  th: ({ children }: { children?: ReactNode }) => (
    <th className="border border-gray-200 px-4 py-2 text-left text-sm font-semibold text-[#051923]">
      {children}
    </th>
  ),
  td: ({ children }: { children?: ReactNode }) => (
    <td className="border border-gray-200 px-4 py-2 text-sm">{children}</td>
  ),
  blockquote: ({ children }: { children?: ReactNode }) => {
    const extractText = (node: ReactNode): string => {
      if (typeof node === 'string') return node;
      if (typeof node === 'number') return String(node);
      if (!node) return '';
      if (Array.isArray(node)) return node.map(extractText).join('');
      if (typeof node === 'object' && 'props' in node) {
        return extractText((node as { props: { children?: ReactNode } }).props.children);
      }
      return '';
    };
    const text = extractText(children);
    // A positive HMRC identity-verified advisory gets a clear green treatment so
    // it stands out from the cautionary (amber) and neutral blockquotes.
    const isVerified = text.includes('HMRC IDENTITY VERIFIED');
    // The "Please note" guidance and cautionary advisories share an amber style.
    const isCaution =
      !isVerified &&
      (text.includes('Please note:') ||
        text.includes('NOT ON RECORD') ||
        text.includes('PARTIAL DATA MATCH') ||
        text.includes('DISSOLVED COMPANY') ||
        text.includes('DISQUALIFIED DIRECTOR') ||
        text.includes('YOUNG COMPANY') ||
        text.includes('SOURCE OF FUNDS MISMATCH'));
    if (isVerified) {
      return (
        <blockquote className="my-4 rounded-md border-l-4 border-green-500 bg-green-50 px-4 py-3 text-sm text-green-900">
          {children}
        </blockquote>
      );
    }
    if (isCaution) {
      return (
        <blockquote className="my-4 rounded-md border-l-4 border-amber-400 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {children}
        </blockquote>
      );
    }
    return (
      <blockquote className="my-2 border-l-4 border-gray-300 pl-4 text-sm">{children}</blockquote>
    );
  },
};

export function ScreeningLogSection({ markdown, onOpenAuditTrail }: ScreeningLogSectionProps) {
  return (
    <section className="mb-8 rounded-lg border bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-[#051923]">Screening Log</h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="text-[16px] font-semibold leading-none text-[#131313] underline"
            onClick={onOpenAuditTrail}
          >
            View AI Audit Trail
          </button>
          <Tooltip>
            <TooltipTrigger type="button">
              <Info className="h-4 w-4 text-gray-400" />
            </TooltipTrigger>
            <TooltipContent>
              <p>View the complete AI reasoning and decision-making process for this screening</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
      <div className="prose prose-sm max-w-none text-gray-700">
        {markdown ? (
          isFailClosed(markdown) ? (
            <FailClosedNotice fieldLabel="Screening log" />
          ) : (
            <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {cleanFormattedReport(markdown)}
            </Markdown>
          )
        ) : (
          <p className="text-gray-500">No screening log available.</p>
        )}
      </div>
    </section>
  );
}
